import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useNavigate } from 'react-router'
import moment from 'moment'
import clsx from 'classnames'
import { AppointmentStatus, type AppointmentResponse, type Salon } from 'api/api.types'
import { APIService } from 'api/APIService'
import { setSelectedCityId } from 'src/store/app.state.store'
import { initAppointment } from 'src/store/appointment.store'
import { usePopulatedAppointments, type PopulatedAppointment } from 'src/hooks/usePopulatedAppointments'
import { usePagination } from 'src/hooks/usePagination'
import { Loader } from 'components/Utility/Loader/Loader'
import { StatusModal } from 'components/Utility/StatusModal/StatusModal'
import { AppButton } from 'components/FormElements/AppButton/AppButton'
import { CurrentVisit } from './CurrentVisit'
import { PastVisit } from './PastVisit'
import { CancelModal, type CancelForm } from './CancelModal'
import { RateModal, type RateForm } from './RateModal'
import styles from './Visits.module.scss'

const PER_PAGE = 10
/** A visit can be rated for two weeks */
const REVIEW_PERIOD_WEEKS = 2

/** Services of a visit that are still in the catalogue, as booking requests */
const bookableServices = (visit: PopulatedAppointment) => visit.services
  .filter((service) => !!service.meta)
  .map((service) => ({ id: service.id, amount: service.amount }))

const errorMessage = (e: unknown) => (e instanceof Error ? e.message : String(e))

/** «Мои записи»: upcoming and past visits with cancel, change, repeat and rating */
export const Visits: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [visits, setVisits] = useState<AppointmentResponse[]>([])
  const [visitsPage, setVisitsPage] = useState(0)
  const [visitsCount, setVisitsCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [cancelVisitId, setCancelVisitId] = useState<number | null>(null)
  const [cancelSubmitting, setCancelSubmitting] = useState(false)
  const [rateVisitId, setRateVisitId] = useState<number | null>(null)
  const [rateSubmitting, setRateSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const populatedVisits = usePopulatedAppointments(visits)
  const futureVisits = useMemo(
    () => populatedVisits.filter((v) => v.status !== AppointmentStatus.completed),
    [populatedVisits],
  )
  const pastVisits = useMemo(
    () => populatedVisits.filter((v) => v.status === AppointmentStatus.completed),
    [populatedVisits],
  )

  const loadAppointments = useCallback(async () => {
    setLoading(true)
    try {
      const { appointments, total } = await APIService.profile.fetchVisits(visitsPage, PER_PAGE)
      setVisitsCount(total)
      setVisits(appointments)
    } finally {
      setLoading(false)
    }
  }, [visitsPage])

  useEffect(() => {
    loadAppointments()
  }, [loadAppointments])

  const handlePhoneClick = useCallback((e: React.MouseEvent, salon: Salon | null) => {
    e.stopPropagation()
    // The web salon list has no phone of its own; it is on the salon page
    const phone = salon?.meta?.templateVars?.pageVars?.salonPhones?.[0]?.phone
    if (phone) {
      document.location.href = `tel:+${phone}`
    }
  }, [])

  const handleChangeClick = useCallback((visit: PopulatedAppointment) => {
    dispatch(setSelectedCityId(visit.cityId))
    dispatch(initAppointment({
      id: visit.id,
      salonId: visit.salon ? visit.salonId : null,
      services: bookableServices(visit),
      date: visit.date,
      time: visit.time,
      comment: visit.comment,
    }))
    navigate('#appointment')
  }, [dispatch, navigate])

  const handleRepeatClick = useCallback((visit: PopulatedAppointment) => {
    dispatch(setSelectedCityId(visit.cityId))
    dispatch(initAppointment({
      salonId: visit.salonId,
      services: bookableServices(visit),
    }))
    navigate('#appointment')
  }, [dispatch, navigate])

  const handleCancelSubmit = useCallback(async ({ reason, comment }: CancelForm) => {
    if (cancelVisitId === null) {
      return
    }
    setCancelSubmitting(true)
    try {
      await APIService.profile.cancelVisit(cancelVisitId, reason, comment)
      await loadAppointments()
      setCancelVisitId(null)
    } catch (e) {
      setSubmitError(errorMessage(e))
    } finally {
      setCancelSubmitting(false)
    }
  }, [cancelVisitId, loadAppointments])

  const handleRateSubmit = useCallback(async ({ answers, comment }: RateForm) => {
    if (rateVisitId === null) {
      return
    }
    setRateSubmitting(true)
    try {
      await APIService.profile.sendReview(rateVisitId, answers, comment)
      await loadAppointments()
      setRateVisitId(null)
    } catch (e) {
      setSubmitError(errorMessage(e))
    } finally {
      setRateSubmitting(false)
    }
  }, [rateVisitId, loadAppointments])

  const dismissSubmitError = useCallback(() => setSubmitError(null), [])

  const renderPage = useCallback((pageIndex: number) => (
    <div key={pageIndex} className={styles.page}>
      <span
        className={clsx([styles.pageLink, { [styles.active]: pageIndex === visitsPage }])}
        onClick={() => setVisitsPage(pageIndex)}
      >
        {pageIndex + 1}
      </span>
    </div>
  ), [visitsPage])

  const pages = usePagination(renderPage, visitsCount, PER_PAGE, visitsPage)
  const hasVisits = futureVisits.length > 0 || pastVisits.length > 0
  const reviewDeadline = moment().subtract(REVIEW_PERIOD_WEEKS, 'weeks')

  return (
    <div className={styles.container}>
      <div className={styles.allVisitsContainer}>
        {loading && <Loader />}
        {!hasVisits && (
          <div className={styles.noVisits}>
            <div>У Вас ещё не было ни одной записи,<br /> но это легко исправить!</div>
            <br />
          </div>
        )}
        {futureVisits.length > 0 && (
          <>
            <div className={styles.delimiter}>Предстоящие визиты</div>
            <div className={styles.visitsContainer}>
              {futureVisits.map((visit) => <CurrentVisit
                key={visit.id}
                visit={visit}
                className={styles.currentVisit}
                onClickCancel={() => setCancelVisitId(visit.id)}
                onClickCall={(e) => handlePhoneClick(e, visit.salon)}
                onClickChange={() => handleChangeClick(visit)}
              />)}
            </div>
          </>
        )}
        {pastVisits.length > 0 && (
          <>
            <div className={styles.delimiter}>Прошедшие визиты</div>
            <div className={styles.visitsContainer}>
              {pastVisits.map((visit) => <PastVisit
                key={visit.id}
                visit={visit}
                reviewDisabled={moment(visit.date).isBefore(reviewDeadline)}
                onClickRepeat={() => handleRepeatClick(visit)}
                onClickReview={() => setRateVisitId(visit.id)}
              />)}
            </div>
          </>
        )}
        {(hasVisits && pages.length > 1) && (
          <div className={styles.pages}>
            {pages}
          </div>
        )}
      </div>
      <div className={styles.noVisits}>
        <Link to={'#appointment'}>
          <AppButton>Записаться</AppButton>
        </Link>
      </div>
      <CancelModal
        submitting={cancelSubmitting}
        isOpen={cancelVisitId !== null}
        onClose={() => setCancelVisitId(null)}
        onSubmit={handleCancelSubmit}
      />
      <RateModal
        submitting={rateSubmitting}
        isOpen={rateVisitId !== null}
        onClose={() => setRateVisitId(null)}
        onSubmit={handleRateSubmit}
      />
      <StatusModal
        isOpen={submitError !== null}
        modalText={submitError}
        status={'error'}
        buttonText={'ОК'}
        onClose={dismissSubmitError}
        onButtonClick={dismissSubmitError}
      />
    </div>
  )
}
